import React, { useEffect, useState } from 'react';
import api from '../api';
import { format } from 'date-fns';
import { useAuth } from '../context/AuthContext';

const STATUS_LABELS = {
  pending: 'Pending',
  under_review: 'Under Review',
  approved: 'Approved',
  declined: 'Declined',
};

const STATUS_COLOR = {
  pending: '#E09A10',
  under_review: '#1270B8',
  approved: '#0FA86A',
  declined: '#D63A3A',
};

function fmt(n) {
  if (!n && n !== 0) return '—';
  return new Intl.NumberFormat('en-NG', { style: 'currency', currency: 'NGN' }).format(n);
}

export default function SAReports() {
  const { user } = useAuth();
  const [requests, setRequests] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  const isInitiator = user?.role === 'sa_initiator';
  const isApprover = user?.role === 'sa_approver';

  useEffect(() => {
    Promise.all([
      api.get('/api/sa/report'),
      api.get('/api/sa/stats'),
    ]).then(([r, s]) => {
      setRequests(r.data.requests || []);
      setStats(s.data);
    }).catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  const exportCSV = () => {
    const BOM = '\uFEFF';
    const headers = [
      'Request No', 'Account Name', 'Account Number', 'Branch',
      'Amount Requested', 'Amount Approved', 'Previous Salary Month',
      'Previous Salary Amount', 'Status', 'Initiator', 'Approver',
      'Date Submitted', 'Date Decided', 'Notes', 'Approver Notes',
    ];
    const rows = requests.map(r => [
      r.request_number || '',
      r.account_name || '',
      r.account_number || '',
      r.branch || '',
      r.amount_requested || '',
      r.amount_approved || '',
      r.previous_salary_month || '',
      r.previous_salary_amount || '',
      STATUS_LABELS[r.status] || r.status || '',
      r.initiator_name || '',
      r.approver_name || '',
      r.created_at ? format(new Date(r.created_at), 'dd/MM/yyyy HH:mm') : '',
      r.decided_at ? format(new Date(r.decided_at), 'dd/MM/yyyy HH:mm') : '',
      r.notes || '',
      r.approver_notes || '',
    ]);
    const csv = BOM + [headers, ...rows]
      .map(row => row.map(c => `"${String(c).replace(/"/g, '""')}"`).join(','))
      .join('\r\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `OMIYE-MFB-SA-Report-${format(new Date(), 'dd-MM-yyyy')}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  if (loading) return <div className="loading">⏳ Loading SA reports…</div>;

  const totalAmount = requests
    .filter(r => r.status === 'approved')
    .reduce((sum, r) => sum + parseFloat(r.amount_approved || 0), 0);

  const totalRequested = requests
    .reduce((sum, r) => sum + parseFloat(r.amount_requested || 0), 0);

  return (
    <div>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
        <div>
          <div style={{ fontFamily: 'Sora,sans-serif', fontSize: 20, fontWeight: 700, color: 'var(--text)' }}>
            💰 SA Reports & Analytics
          </div>
          <div style={{ fontSize: 12, color: 'var(--text3)', marginTop: 3 }}>
            {isInitiator ? 'Your salary advance request history' : isApprover ? 'Requests assigned to you' : 'All salary advance requests'}
          </div>
        </div>
        <button className="btn btn-success btn-sm" onClick={exportCSV}>
          📥 Export CSV
        </button>
      </div>

      {/* Summary Stats */}
      {stats && (
        <div className="stats-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 12, marginBottom: 20 }}>
          <div className="stat-card stat-sky">
            <div className="stat-label">Total Requests</div>
            <div className="stat-val">{stats.total}</div>
            <div className="stat-sub">All time</div>
          </div>
          <div className="stat-card stat-green">
            <div className="stat-label">Approved</div>
            <div className="stat-val">{stats.approved}</div>
            <div className="stat-sub">
              {stats.total ? Math.round((stats.approved / stats.total) * 100) : 0}% approval rate
            </div>
          </div>
          <div className="stat-card stat-orange">
            <div className="stat-label">Pending / Review</div>
            <div className="stat-val">{(stats.pending || 0) + (stats.under_review || 0)}</div>
            <div className="stat-sub">Awaiting decision</div>
          </div>
          <div className="stat-card stat-red">
            <div className="stat-label">Declined</div>
            <div className="stat-val">{stats.declined}</div>
            <div className="stat-sub">
              {stats.total ? Math.round((stats.declined / stats.total) * 100) : 0}% decline rate
            </div>
          </div>
        </div>
      )}

      {/* Amount Summary */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14, marginBottom: 20 }}>
        <div className="card card-body" style={{ padding: 18 }}>
          <div style={{ fontSize: 11, color: 'var(--text3)', marginBottom: 6, fontWeight: 600, textTransform: 'uppercase' }}>Total Amount Requested</div>
          <div style={{ fontFamily: 'Sora,sans-serif', fontSize: 22, fontWeight: 700, color: 'var(--sky)' }}>{fmt(totalRequested)}</div>
          <div style={{ fontSize: 11, color: 'var(--text3)', marginTop: 4 }}>Across {requests.length} request{requests.length !== 1 ? 's' : ''}</div>
        </div>
        <div className="card card-body" style={{ padding: 18 }}>
          <div style={{ fontSize: 11, color: 'var(--text3)', marginBottom: 6, fontWeight: 600, textTransform: 'uppercase' }}>Total Amount Approved</div>
          <div style={{ fontFamily: 'Sora,sans-serif', fontSize: 22, fontWeight: 700, color: '#0FA86A' }}>{fmt(totalAmount)}</div>
          <div style={{ fontSize: 11, color: 'var(--text3)', marginTop: 4 }}>Across {stats?.approved || 0} approved request{stats?.approved !== 1 ? 's' : ''}</div>
        </div>
      </div>

      {/* Status Breakdown */}
      <div className="card card-body" style={{ marginBottom: 20, padding: 18 }}>
        <div className="section-title" style={{ fontSize: 12, marginBottom: 14 }}>Requests by Status</div>
        {Object.entries(STATUS_LABELS).map(([key, label]) => {
          const count = (stats && stats[key]) || 0;
          const pct = stats?.total ? Math.round((count / stats.total) * 100) : 0;
          return (
            <div key={key} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '7px 0', borderBottom: '1px solid var(--border)' }}>
              <span style={{
                background: STATUS_COLOR[key] + '22', color: STATUS_COLOR[key],
                padding: '3px 10px', borderRadius: 12, fontSize: 11, fontWeight: 600, minWidth: 100
              }}>{label}</span>
              <div style={{ flex: 1 }}>
                <div className="sla-bar">
                  <div className="sla-fill" style={{ width: `${pct}%`, background: STATUS_COLOR[key] }} />
                </div>
              </div>
              <span style={{ fontSize: 13, fontWeight: 600, fontFamily: 'Sora', minWidth: 24, textAlign: 'right' }}>{count}</span>
              <span style={{ fontSize: 11, color: 'var(--muted)', minWidth: 34 }}>{pct}%</span>
            </div>
          );
        })}
      </div>

      {/* All Requests Table */}
      <div className="section-head" style={{ marginBottom: 10 }}>
        <div className="section-title">📋 All SA Requests</div>
        <button className="btn btn-success btn-sm" onClick={exportCSV}>📥 Export CSV</button>
      </div>
      <div className="table-wrap">
        <table>
          <thead>
            <tr>
              <th>Req No.</th>
              <th>Account</th>
              <th>Branch</th>
              {!isInitiator && <th>Initiator</th>}
              {!isApprover && <th>Approver</th>}
              <th>Requested</th>
              <th>Approved</th>
              <th>Status</th>
              <th>Date</th>
            </tr>
          </thead>
          <tbody>
            {requests.length === 0 && (
              <tr>
                <td colSpan={isInitiator || isApprover ? 7 : 9} style={{ textAlign: 'center', color: 'var(--muted)', padding: 30 }}>
                  No SA requests found
                </td>
              </tr>
            )}
            {requests.map(r => (
              <tr key={r.id} style={{ cursor: 'default' }}>
                <td>
                  <span style={{ fontFamily: 'Sora', fontSize: 11, fontWeight: 600, color: 'var(--sky-dark)' }}>
                    {r.request_number}
                  </span>
                </td>
                <td>
                  <div style={{ fontSize: 12, fontWeight: 500 }}>{r.account_name}</div>
                  <div style={{ fontSize: 11, color: 'var(--muted)' }}>{r.account_number}</div>
                </td>
                <td style={{ fontSize: 11, color: 'var(--muted)' }}>{r.branch}</td>
                {!isInitiator && <td style={{ fontSize: 11 }}>{r.initiator_name || '—'}</td>}
                {!isApprover && <td style={{ fontSize: 11 }}>{r.approver_name || '—'}</td>}
                <td style={{ fontSize: 12, fontWeight: 600 }}>{fmt(r.amount_requested)}</td>
                <td style={{ fontSize: 12, fontWeight: 600, color: '#0FA86A' }}>{fmt(r.amount_approved)}</td>
                <td>
                  <span style={{
                    background: (STATUS_COLOR[r.status] || '#999') + '22',
                    color: STATUS_COLOR[r.status] || '#999',
                    padding: '3px 10px', borderRadius: 12, fontSize: 11, fontWeight: 600
                  }}>
                    {STATUS_LABELS[r.status] || r.status}
                  </span>
                </td>
                <td style={{ fontSize: 11, color: 'var(--muted)', whiteSpace: 'nowrap' }}>
                  {r.created_at ? format(new Date(r.created_at), 'dd MMM yyyy') : '—'}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
